import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Avatar,
  Fade,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import GroupAddIcon from "@material-ui/icons/GroupAdd";
import PersonIcon from "@material-ui/icons/Person";
import WarningIcon from "@material-ui/icons/Warning";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

const MAX_PARTICIPANTS = 257;
const MAX_NAME_LENGTH = 100;
const WARN_THRESHOLD = 240;

/** Extrai números internacionais de texto colado (linha, vírgula, ponto-e-vírgula, tab ou espaço). */
function parseNumbersFromText(raw) {
  if (!raw || !String(raw).trim()) return [];
  const seen = new Set();
  const out = [];
  const chunks = String(raw).split(/[\n\r,;|\t]+/);
  for (const chunk of chunks) {
    const parts = chunk.trim().split(/\s+/).filter(Boolean);
    const items = parts.length > 1 ? parts : [chunk];
    for (const part of items) {
      const n = part.replace(/\D/g, "").trim();
      if (n.length >= 10 && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    }
  }
  return out;
}

const t = (key, fallback) => {
  const val = i18n.t(`groupManagement.${key}`);
  if (val === `groupManagement.${key}`) return fallback || val;
  return val;
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 600,
    margin: "0 auto",
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: 16,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: `1px solid ${theme.palette.divider}`,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    "&::before": {
      content: '""',
      width: 4,
      height: 20,
      backgroundColor: theme.palette.primary.main,
      marginRight: theme.spacing(1),
      borderRadius: 2,
    },
  },
  sectionSubtitle: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    marginLeft: theme.spacing(1.5),
  },
  field: {
    marginBottom: theme.spacing(1),
  },
  charCount: {
    textAlign: "right",
    fontSize: "0.75rem",
    color: theme.palette.text.hint,
    marginBottom: theme.spacing(1),
  },
  charCountWarn: {
    color: theme.palette.warning.main,
    fontWeight: 600,
  },
  addRow: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: "flex-start",
  },
  addButton: {
    minWidth: 48,
    height: 40,
    borderRadius: 10,
  },
  coverSection: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  coverDropZone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: 140,
    border: "2px dashed",
    borderColor: theme.palette.grey[300],
    borderRadius: 16,
    padding: theme.spacing(3),
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: theme.palette.grey[50],
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.main + "08",
    },
  },
  coverDropZoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main + "12",
    transform: "scale(1.01)",
  },
  coverPreviewContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 0),
  },
  coverAvatar: {
    width: 120,
    height: 120,
    border: `3px solid ${theme.palette.primary.main}`,
    boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
  },
  coverFileName: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  coverActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  inputFile: { display: "none" },
  participantCounter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
  },
  counterChip: {
    fontWeight: 600,
    fontSize: "0.8rem",
  },
  warningChip: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    fontWeight: 600,
  },
  errorChip: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
    fontWeight: 600,
  },
  participantList: {
    maxHeight: 220,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    marginTop: theme.spacing(1),
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: 3,
      backgroundColor: theme.palette.grey[300],
    },
  },
  participantItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  participantAvatar: {
    width: 32,
    height: 32,
    backgroundColor: theme.palette.primary.main + "18",
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1.5),
  },
  emptyParticipants: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.hint,
  },
  submitButton: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    fontWeight: 600,
    fontSize: "1rem",
    textTransform: "none",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    "&:hover": {
      boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
    },
  },
  nameError: {
    color: theme.palette.error.main,
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(3, 0, 1),
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const GroupNew = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const connectionId = params.get("connectionId");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [newNumber, setNewNumber] = useState("");
  const [participants, setParticipants] = useState([]);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState("");
  const [numberError, setNumberError] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const coverInputRef = useRef(null);
  const numberInputRef = useRef(null);

  useEffect(() => {
    if (!connectionId) history.replace("/group-management");
  }, [connectionId, history]);

  useEffect(() => {
    if (nameTouched && !name.trim()) {
      setNameError(t("nameRequired", "Nome do grupo é obrigatório"));
    } else {
      setNameError("");
    }
  }, [name, nameTouched]);

  const addParticipantsFromText = useCallback(
    (text, { clearInput = true } = {}) => {
      const candidates = parseNumbersFromText(text);
      if (!candidates.length) {
        setNumberError(
          "Informe ao menos um número válido (10+ dígitos, formato internacional)"
        );
        return 0;
      }

      let skippedDup = 0;
      let skippedLimit = 0;
      const toAdd = [];

      for (const n of candidates) {
        if (participants.includes(n) || toAdd.includes(n)) {
          skippedDup++;
          continue;
        }
        if (participants.length + toAdd.length >= MAX_PARTICIPANTS) {
          skippedLimit++;
          continue;
        }
        toAdd.push(n);
      }

      if (!toAdd.length) {
        if (skippedLimit > 0) {
          setNumberError(`Limite máximo de ${MAX_PARTICIPANTS} participantes atingido`);
        } else {
          setNumberError("Todos os números informados já foram adicionados");
        }
        return 0;
      }

      setNumberError("");
      setParticipants((prev) => [...prev, ...toAdd]);
      if (clearInput) setNewNumber("");

      if (skippedLimit > 0) {
        toast.warn(
          `${toAdd.length} adicionado(s). ${skippedLimit} ignorado(s) — limite de ${MAX_PARTICIPANTS}.`
        );
      } else if (toAdd.length > 1) {
        toast.success(`${toAdd.length} participantes adicionados`);
      }

      if (numberInputRef.current) numberInputRef.current.focus();
      return toAdd.length;
    },
    [participants]
  );

  const addParticipant = useCallback(() => {
    addParticipantsFromText(newNumber);
  }, [newNumber, addParticipantsFromText]);

  const handleParticipantsPaste = useCallback(
    (e) => {
      const text = e.clipboardData?.getData("text") || "";
      if (text && /[\n\r,;|\t]/.test(text)) {
        e.preventDefault();
        addParticipantsFromText(text);
      }
    },
    [addParticipantsFromText]
  );

  const removeParticipant = (num) => {
    setParticipants((prev) => prev.filter((p) => p !== num));
  };

  const handleCoverChange = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("coverMustBeImage", "O arquivo deve ser uma imagem"));
      return;
    }
    setCoverFile(file);
    const reader = new FileReader();
    reader.onload = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const handleFileInput = (e) => {
    handleCoverChange(e.target.files?.[0]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleCoverChange(file);
  };

  const clearCover = () => {
    setCoverFile(null);
    setCoverPreview(null);
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNameTouched(true);

    if (!connectionId || !name.trim()) {
      setNameError(t("nameRequired", "Nome do grupo é obrigatório"));
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("participants", JSON.stringify(participants));
      if (description.trim()) formData.append("description", description.trim());
      if (coverFile) formData.append("cover", coverFile);
      await api.post(
        `/group-management/connections/${connectionId}/groups`,
        formData
      );
      toast.success(t("groupCreated", "Grupo criado com sucesso!"));
      history.push(`/group-management/groups?connectionId=${connectionId}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const isNearLimit = participants.length >= WARN_THRESHOLD;
  const isAtLimit = participants.length >= MAX_PARTICIPANTS;

  if (!connectionId) return null;

  return (
    <MainContainer>
      <MainHeader>
        <div className={classes.headerRow}>
          <IconButton
            size="small"
            onClick={() => history.push("/group-management")}
          >
            <ArrowBackIcon />
          </IconButton>
          <Title>{t("createGroup", "Criar Grupo")}</Title>
        </div>
      </MainHeader>

      <Box className={classes.root}>
        <Paper className={classes.paper}>
          <form onSubmit={handleSubmit} noValidate>
            {/* --- SECTION: Group Name --- */}
            <Typography
              className={classes.sectionTitle}
              style={{ marginTop: 0 }}
            >
              {t("groupName", "Nome do Grupo")} *
            </Typography>
            <TextField
              className={classes.field}
              fullWidth
              placeholder={t(
                "groupNamePlaceholder",
                "Digite o nome do grupo..."
              )}
              value={name}
              onChange={(e) => {
                if (e.target.value.length <= MAX_NAME_LENGTH) {
                  setName(e.target.value);
                }
              }}
              onBlur={() => setNameTouched(true)}
              variant="outlined"
              size="small"
              error={!!nameError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {name.trim() && !nameError && (
                      <CheckCircleIcon
                        style={{ color: "#4caf50", fontSize: 18 }}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {nameError ? (
                <Typography className={classes.nameError}>
                  {nameError}
                </Typography>
              ) : (
                <span />
              )}
              <Typography
                className={`${classes.charCount} ${
                  name.length >= MAX_NAME_LENGTH - 10
                    ? classes.charCountWarn
                    : ""
                }`}
              >
                {name.length}/{MAX_NAME_LENGTH}
              </Typography>
            </Box>

            {/* --- SECTION: Description --- */}
            <Typography className={classes.sectionTitle}>
              {t("groupDescription", "Descrição")}
            </Typography>
            <Typography className={classes.sectionSubtitle}>
              Opcional - Aparecerá nas informações do grupo
            </Typography>
            <TextField
              className={classes.field}
              fullWidth
              multiline
              rows={3}
              placeholder={t(
                "groupDescriptionPlaceholder",
                "Adicione uma descrição para o grupo..."
              )}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              variant="outlined"
              size="small"
            />

            <Divider className={classes.divider} />

            {/* --- SECTION: Cover Photo --- */}
            <Typography className={classes.sectionTitle}>
              {t("groupCoverPhoto", "Foto de Capa")}
            </Typography>
            <Typography className={classes.sectionSubtitle}>
              Opcional - Arraste ou clique para selecionar
            </Typography>
            <div className={classes.coverSection}>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className={classes.inputFile}
                onChange={handleFileInput}
                id="group-cover-input"
              />
              {!coverPreview ? (
                <label
                  htmlFor="group-cover-input"
                  className={`${classes.coverDropZone} ${
                    dragActive ? classes.coverDropZoneActive : ""
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <PhotoCameraIcon
                    style={{
                      fontSize: 48,
                      color: dragActive ? "#1976d2" : "#bdbdbd",
                      marginBottom: 8,
                    }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {t("addCoverPhoto", "Adicionar foto de capa")}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginTop: 4 }}
                  >
                    JPG, PNG ou WEBP
                  </Typography>
                </label>
              ) : (
                <Fade in>
                  <div className={classes.coverPreviewContainer}>
                    <Avatar
                      src={coverPreview}
                      alt="Capa do grupo"
                      className={classes.coverAvatar}
                    />
                    <Typography className={classes.coverFileName}>
                      {coverFile?.name}
                    </Typography>
                    <div className={classes.coverActions}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="default"
                        onClick={() => coverInputRef.current?.click()}
                        startIcon={<PhotoCameraIcon />}
                      >
                        Alterar
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={clearCover}
                        startIcon={<CloseIcon />}
                      >
                        {t("removeCover", "Remover")}
                      </Button>
                    </div>
                  </div>
                </Fade>
              )}
            </div>

            <Divider className={classes.divider} />

            {/* --- SECTION: Participants --- */}
            <Typography className={classes.sectionTitle}>
              {t("participants", "Participantes")}
            </Typography>

            <div className={classes.participantCounter}>
              <Chip
                icon={<PersonIcon />}
                label={`${participants.length}/${MAX_PARTICIPANTS}`}
                size="small"
                className={`${classes.counterChip} ${
                  isAtLimit
                    ? classes.errorChip
                    : isNearLimit
                    ? classes.warningChip
                    : ""
                }`}
                color={
                  isAtLimit
                    ? "default"
                    : isNearLimit
                    ? "default"
                    : "primary"
                }
                variant={isAtLimit || isNearLimit ? "default" : "outlined"}
              />
              {isNearLimit && !isAtLimit && (
                <Chip
                  icon={<WarningIcon style={{ fontSize: 16 }} />}
                  label="Aproximando do limite"
                  size="small"
                  className={classes.warningChip}
                  variant="default"
                />
              )}
              {isAtLimit && (
                <Chip
                  icon={<WarningIcon style={{ fontSize: 16 }} />}
                  label="Limite atingido!"
                  size="small"
                  className={classes.errorChip}
                  variant="default"
                />
              )}
            </div>

            <div className={classes.addRow}>
              <TextField
                fullWidth
                size="small"
                placeholder="5511999999999"
                value={newNumber}
                inputRef={numberInputRef}
                onChange={(e) => {
                  setNewNumber(e.target.value);
                  if (numberError) setNumberError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addParticipant();
                  }
                }}
                onPaste={handleParticipantsPaste}
                variant="outlined"
                error={!!numberError}
                helperText={
                  numberError ||
                  "Um número ou cole uma lista (linha, vírgula ou ponto-e-vírgula) — DDI + DDD + número"
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon
                        style={{ color: "#bdbdbd", fontSize: 20 }}
                      />
                    </InputAdornment>
                  ),
                }}
                disabled={isAtLimit}
              />
              <Tooltip title="Adicionar participante" arrow>
                <span>
                  <Button
                    className={classes.addButton}
                    variant="contained"
                    color="primary"
                    onClick={addParticipant}
                    disabled={isAtLimit || !newNumber.trim()}
                  >
                    <AddIcon />
                  </Button>
                </span>
              </Tooltip>
            </div>

            {participants.length > 0 ? (
              <div className={classes.participantList}>
                <List dense disablePadding>
                  {participants.map((num, index) => (
                    <ListItem
                      key={num}
                      className={classes.participantItem}
                    >
                      <Avatar className={classes.participantAvatar}>
                        <PersonIcon style={{ fontSize: 18 }} />
                      </Avatar>
                      <ListItemText
                        primary={num}
                        secondary={`Participante #${index + 1}`}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Remover" arrow>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => removeParticipant(num)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </div>
            ) : (
              <div className={classes.emptyParticipants}>
                <GroupAddIcon
                  style={{ fontSize: 40, marginBottom: 8, opacity: 0.4 }}
                />
                <Typography variant="body2">
                  Nenhum participante adicionado
                </Typography>
                <Typography variant="caption">
                  Adicione um número ou cole vários de uma vez
                </Typography>
              </div>
            )}

            {/* --- Submit --- */}
            <Button
              className={classes.submitButton}
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading || !name.trim()}
              fullWidth
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <GroupAddIcon />
                )
              }
            >
              {loading
                ? "Criando grupo..."
                : t("create", "Criar Grupo")}
            </Button>
          </form>
        </Paper>
      </Box>
    </MainContainer>
  );
};

export default GroupNew;
